<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Input from '../../../../../components-next/input/Input.vue';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';
import RadioGroup from './RadioGroup.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';
import Switch from '../../../../../components-next/switch/Switch.vue';

const props = defineProps({
  initialName: {
    type: String,
    default: 'Sofia',
  },
  initialVoice: {
    type: String,
    default: 'female',
  },
  initialRole: {
    type: String,
    default: '',
  },
  initialMessageGrouping: {
    type: Number,
    default: 25,
  },
  initialAudioEnabled: {
    type: Boolean,
    default: true,
  },
  initialRandomness: {
    type: Number,
    default: 40,
  },
  initialCustomImageInstructionsEnabled: {
    type: Boolean,
    default: false,
  },
  initialImagePrompt: {
    type: String,
    default: '',
  }
});

const route = useRoute();
const isEditMode = computed(() => route.query.mode === 'edit');

const emit = defineEmits(['update', 'validation']);

const agentName = ref(props.initialName);
const voiceType = ref(props.initialVoice);
const agentRole = ref(props.initialRole);
const messageGrouping = ref(props.initialMessageGrouping);
const messageGroupingEnabled = ref(props.initialMessageGrouping > 0);
const audioEnabled = ref(props.initialAudioEnabled);
const randomness = ref(props.initialRandomness);
const randomnessMode = ref('preset'); // 'preset' ou 'custom'
const customRandomness = ref(40);
const customImageInstructionsEnabled = ref(props.initialCustomImageInstructionsEnabled);
const imagePrompt = ref(props.initialImagePrompt);
const imagePromptIsTouched = ref(false);
const isTouched = ref(false);
const roleIsTouched = ref(false);
const nameIsValid = ref(false);
const roleIsValid = ref(false);
const imagePromptIsValid = ref(true); // Campo opcional, sempre válido

// Flag para a animação do botão de informação da função
const isRoleButtonHovered = ref(false);

// Estado do dropdown customizado
const isDropdownOpen = ref(false);
const selectedGroupingLabel = ref('25 segundos');
const isRandomnessDropdownOpen = ref(false);
const selectedRandomnessLabel = ref('40%');

const voiceOptions = [
  { id: 'female', name: 'Feminina' },
  { id: 'male',   name: 'Masculina' },
];

const messageGroupingOptions = [
  { value: 15, label: '15 segundos' },
  { value: 25, label: '25 segundos' },
  { value: 35, label: '35 segundos' },
];

const randomnessOptions = [
  { value: 40, label: '40%' },
  { value: 70, label: '70%' },
  { value: 100, label: 'Sempre' },
  { value: 'custom', label: 'Personalizado' }
];

// Computar o valor final de aleatoriedade
const finalRandomness = computed(() => {
  if (randomnessMode.value === 'custom') {
    return customRandomness.value;
  }
  return randomness.value;
});

// Computed para mostrar o valor atual do slider
const currentRandomnessValue = computed(() => {
  if (randomnessMode.value === 'custom') {
    return 'Personalizado';
  }
  return selectedRandomnessLabel.value;
});

// Computed para a porcentagem do progresso do slider
const sliderProgress = computed(() => {
  return ((customRandomness.value - 10) / (100 - 10)) * 100;
});

watch([agentName, voiceType, agentRole, messageGrouping, messageGroupingEnabled, audioEnabled, finalRandomness, customImageInstructionsEnabled, imagePrompt, nameIsValid, roleIsValid, imagePromptIsValid], () => {
  const valid = nameIsValid.value && roleIsValid.value && imagePromptIsValid.value;

  emit('update', {
    name: agentName.value,
    voice: voiceType.value,
    role: agentRole.value,
    messageGrouping: messageGroupingEnabled.value ? messageGrouping.value : 0,
    audioEnabled: audioEnabled.value,
    randomness: finalRandomness.value,
    customImageInstructionsEnabled: customImageInstructionsEnabled.value,
    imagePrompt: imagePrompt.value,
    isValid: valid
  });
  emit('validation', valid);
}, { deep: true });

watch(() => props.initialName, newVal => {
  if (newVal !== agentName.value) {
    agentName.value = newVal;
    // No validation needed here - handled by ValidationFeedback component
  }
});
watch(() => props.initialVoice, newVal => {
  if (newVal !== voiceType.value) voiceType.value = newVal;
});
watch(() => props.initialRole, newVal => {
  if (newVal !== agentRole.value) {
    agentRole.value = newVal;
    // No validation needed here - handled by ValidationFeedback component
  }
});

watch(() => props.initialMessageGrouping, newVal => {
  if (newVal !== messageGrouping.value) {
    messageGrouping.value = newVal;
    messageGroupingEnabled.value = newVal > 0;
    // Atualizar o label também
    const option = messageGroupingOptions.find(opt => opt.value === newVal);
    if (option && newVal > 0) {
      selectedGroupingLabel.value = option.label;
    }
  }
});

watch(() => props.initialAudioEnabled, newVal => {
  if (newVal !== audioEnabled.value) audioEnabled.value = newVal;
});

watch(() => props.initialRandomness, newVal => {
  if (newVal !== randomness.value) {
    randomness.value = newVal;
    // Atualizar o label do dropdown
    const option = randomnessOptions.find(opt => opt.value === newVal);
    if (option) {
      selectedRandomnessLabel.value = option.label;
    } else {
      // Se não é um preset, é custom
      randomnessMode.value = 'custom';
      customRandomness.value = newVal;
      selectedRandomnessLabel.value = 'Personalizado';
    }
  }
});

watch(() => props.initialCustomImageInstructionsEnabled, newVal => {
  if (newVal !== customImageInstructionsEnabled.value) {
    customImageInstructionsEnabled.value = newVal;
  }
});

// Watch para truncar o texto quando o campo for desabilitado
watch(customImageInstructionsEnabled, (newVal) => {
  // Se o campo foi desabilitado e o texto excede 2000 caracteres, truncar
  if (!newVal && imagePrompt.value && imagePrompt.value.length > 2000) {
    imagePrompt.value = imagePrompt.value.substring(0, 2000);
  }
  
  // Se o campo foi desabilitado, marcar a validação como válida
  if (!newVal) {
    imagePromptIsValid.value = true;
  }
});

watch(() => props.initialImagePrompt, newVal => {
  if (newVal !== imagePrompt.value) {
    imagePrompt.value = newVal;
  }
});

const handleBlur = () => {
  isTouched.value = true;
};

const handleRoleBlur = () => {
  roleIsTouched.value = true;
};

const handleImagePromptBlur = () => {
  imagePromptIsTouched.value = true;
};

const handleNameValidation = (isValid) => {
  nameIsValid.value = isValid;
};

const handleRoleValidation = (isValid) => {
  roleIsValid.value = isValid;
};

const handleImagePromptValidation = (isValid) => {
  imagePromptIsValid.value = isValid;
};

const handleRoleInfoClick = () => {
  isRoleButtonHovered.value = true;
  useAlert(
    'A função do agente define como ele se apresentará nas interações com os clientes. Descreva o papel específico, como "Assistente de Atendimento ao Cliente" ou "Especialista em Suporte Técnico".',
    { type: 'info', duration: 7000 }
  );
};

// Funções do dropdown customizado
const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const selectGrouping = (option) => {
  messageGrouping.value = option.value;
  selectedGroupingLabel.value = option.label;
  isDropdownOpen.value = false;
};

// Funções do dropdown de aleatoriedade
const toggleRandomnessDropdown = () => {
  isRandomnessDropdownOpen.value = !isRandomnessDropdownOpen.value;
};

const selectRandomness = (option) => {
  if (option.value === 'custom') {
    randomnessMode.value = 'custom';
    selectedRandomnessLabel.value = option.label;
    // Garantir que customRandomness tem um valor válido
    if (!customRandomness.value || customRandomness.value < 10 || customRandomness.value > 100) {
      customRandomness.value = 40;
    }
  } else {
    randomnessMode.value = 'preset';
    randomness.value = option.value;
    selectedRandomnessLabel.value = option.label;
  }
  isRandomnessDropdownOpen.value = false;
};

// Fechar dropdown ao clicar fora
const handleClickOutside = (event) => {
  const dropdownGrouping = event.target.closest('.custom-dropdown-grouping');
  const dropdownRandomness = event.target.closest('.custom-dropdown-randomness');
  
  if (!dropdownGrouping) {
    isDropdownOpen.value = false;
  }
  if (!dropdownRandomness) {
    isRandomnessDropdownOpen.value = false;
  }
};

// Initial validation on mount
onMounted(() => {
  // No validation needed here - handled by ValidationFeedback component
  // ValidationFeedback will emit validation results that we capture
  
  // Inicializar label do dropdown de agrupamento
  if (messageGrouping.value > 0) {
    const initialOption = messageGroupingOptions.find(opt => opt.value === messageGrouping.value);
    if (initialOption) {
      selectedGroupingLabel.value = initialOption.label;
    }
  } else {
    // Se for 0, desabilitar e usar valor padrão de 25
    messageGroupingEnabled.value = false;
    messageGrouping.value = 25;
    selectedGroupingLabel.value = '25 segundos';
  }
  
  // Inicializar label do dropdown de aleatoriedade
  const initialRandomnessOption = randomnessOptions.find(opt => opt.value === randomness.value);
  if (initialRandomnessOption) {
    selectedRandomnessLabel.value = initialRandomnessOption.label;
  } else {
    selectedRandomnessLabel.value = 'Personalizado';
  }
  
  // Adicionar listener para cliques fora do dropdown
  document.addEventListener('click', handleClickOutside);
});

// Cleanup
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="space-y-4">
    <!-- Campo Nome do Agente -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between">
        <label class="text-sm font-medium text-n-slate-12">Nome do Agente *</label>
        <span v-if="agentName" class="text-xs text-n-slate-10">{{ agentName.length }}/50</span>
      </div>
      <Input
        v-model="agentName"
        placeholder="Digite o nome do seu agente"
        required
        @blur="handleBlur"
        :maxlength="50"
      />
      <ValidationFeedback 
        :value="agentName"
        field-type="agentName"
        :touched="isTouched"
        :helper-text="'Use entre 3 e 50 caracteres (letras, números, espaço, ., _ ou -)'"
        :show-char-count="true"
        :current-length="agentName.length"
        :max-length="50"
        :min-length="3"
        @validation="handleNameValidation"
      />
    </div>

    <!-- Campo Função do Agente -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <label class="text-sm font-medium text-n-slate-12">Função do Agente *</label>
          <button
          class="inline-flex items-center justify-center w-5 h-5 p-0 text-xs border-0 rounded-full outline-none text-n-slate-11 info-btn"
          :class="{ 'pulse-animation': !isRoleButtonHovered }"
          @mouseenter="isRoleButtonHovered = true"
          @click="handleRoleInfoClick"
          v-tooltip.top="'Descreva a função que seu agente desempenhará, como Assistente de Vendas, Suporte Técnico, etc.'"
        >
            <span class="i-lucide-info"></span>
          </button>
        </div>
        <span v-if="agentRole" class="text-xs text-n-slate-10">{{ agentRole.length }}/300</span>
      </div>
      <Textarea
        v-model="agentRole"
        :placeholder="isEditMode ? 'Ex: Suporte ao Cliente, Assistente de Vendas' : 'Ex: Agente de qualificação comercial especializado em soluções de ... Minha missão é coletar os dados necessários do cliente e, caso ele solicite, demonstrar ... Após a qualificação, devo redirecioná-lo a um atendente humano.'"
        required
        @blur="handleRoleBlur"
        :maxlength="300"
        :autoHeight="true"
        :resize="true"
        :minHeight="'2.5rem'"
        :maxHeight="'4.5rem'"
        :showCharacterCount="false"
      />
      <ValidationFeedback 
        :value="agentRole"
        :field-type="'agentRole'"
        :touched="roleIsTouched"
        :helper-text="'Entre 150-300 caracteres. Apenas letras, números, espaços, vírgulas, pontos, hífens e quebras de linha.'"
        :show-helper="true"
        :show-char-count="true"
        :current-length="agentRole.length"
        :max-length="300"
        :min-length="150"
        @validation="handleRoleValidation"
      />
    </div>

    <!-- Tipo de Voz -->
    <div class="p-4 bg-n-slate-2 dark:bg-n-slate-3 rounded-lg border border-n-slate-4">
      <label class="text-sm font-medium text-n-slate-12 mb-2 block">Tipo de Voz</label>
      <RadioGroup v-model="voiceType" :options="voiceOptions" name="voice-type" />
      <div class="text-xs text-n-slate-10 mt-2">
        Escolha o tipo de voz para seu agente de atendimento.
      </div>
    </div>

    <!-- Habilitar Áudio -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-n-slate-12">Habilitar Áudio</label>
        <Switch v-model="audioEnabled" />
      </div>
      <p class="text-n-slate-10 text-sm mt-1">
        <span class="i-lucide-volume-2 mr-1"></span>
        Permitir que o agente envie respostas em áudio
      </p>
      
      <!-- Conteúdo condicional do áudio -->
      <div v-if="audioEnabled" class="mt-3 p-4 bg-n-slate-2 dark:bg-n-slate-3 rounded-lg border border-n-slate-4 animate-fade-in">
        <label class="text-sm font-medium text-n-slate-12 mb-2 block">Selecione a Aleatoriedade</label>
        
        <!-- Dropdown de aleatoriedade -->
        <div class="relative w-fit custom-dropdown-randomness">
          <button
            @click="toggleRandomnessDropdown"
            type="button"
            class="flex items-center justify-between px-3 py-2 text-sm border rounded-lg bg-n-slate-1 border-n-slate-6 text-n-slate-12 hover:border-primary-600 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 focus:outline-none transition-colors cursor-pointer min-w-[240px] dark:bg-n-slate-1 dark:border-n-slate-6 dark:text-n-slate-12 dark:hover:border-primary-600 dark:focus:border-primary-600 dark:focus:ring-primary-600/20"
          >
            <span>{{ currentRandomnessValue }}</span>
            <span 
              class="i-lucide-chevron-up ml-2 h-4 w-4 text-n-slate-10 transition-transform"
              :class="{ 'rotate-180': !isRandomnessDropdownOpen }"
            ></span>
          </button>
          
          <div
            v-if="isRandomnessDropdownOpen"
            class="absolute bottom-full mb-1 left-0 w-full bg-n-slate-1 border border-n-slate-6 rounded-lg shadow-lg z-50 overflow-hidden"
          >
            <button
              v-for="option in randomnessOptions"
              :key="option.label"
              @click="selectRandomness(option)"
              type="button"
              class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-3 transition-colors op-select"
              :class="{ 'bg-n-slate-3': (option.value === 'custom' && randomnessMode === 'custom') || (option.value !== 'custom' && randomness === option.value) }"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        
        <!-- Controle personalizado do slider -->
        <div v-if="randomnessMode === 'custom'" class="mt-3 animate-fade-in">
          <div class="w-full max-w-[240px]">
            <!-- Container do slider com visual consistente -->
            <div class="randomness-slider-container bg-n-slate-1 dark:bg-n-slate-2 border border-n-slate-6 dark:border-n-slate-6 hover:bg-n-slate-2 dark:hover:bg-n-slate-3 hover:border-n-slate-7">
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-medium text-n-slate-12">Valor personalizado</span>
                <span class="text-xs font-semibold text-n-brand">{{ customRandomness }}%</span>
              </div>
              
              <!-- Slider customizado -->
              <div class="relative">
                <input
                  type="range"
                  v-model.number="customRandomness"
                  min="10"
                  max="100"
                  step="1"
                  class="randomness-slider bg-n-slate-4 dark:bg-n-slate-5 
                         [&::-webkit-slider-thumb]:!bg-n-brand [&::-webkit-slider-thumb]:!border-0
                         [&::-moz-range-thumb]:!bg-n-brand
                         [&::-moz-range-track]:bg-n-slate-4 dark:[&::-moz-range-track]:bg-n-slate-5
                         [&::-moz-range-progress]:!bg-n-brand"
                  :style="{ '--progress': `${sliderProgress}%` }"
                />
                
                <!-- Labels de referência -->
                <div class="flex justify-between mt-2">
                  <span class="text-xs text-n-slate-10">10%</span>
                  <span class="text-xs text-n-slate-10">100%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="text-xs text-n-slate-10 mt-2">
          <strong>Importante:</strong> Este valor define apenas a probabilidade do agente <strong>iniciar</strong> a conversa em áudio, não a porcentagem de mensagens em áudio durante a conversa. Se for <strong>sempre</strong> (100%), o agente sempre iniciará em áudio.
        </div>
      </div>
    </div>

    <!-- Instruções Customizadas para Leitura de Imagem -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-n-slate-12">Instruções Customizadas de Imagem</label>
        <Switch v-model="customImageInstructionsEnabled" />
      </div>
      <p class="text-n-slate-10 text-sm mt-1">
        <span class="i-lucide-image mr-1"></span>
        Adicionar instruções específicas para quando o agente analisar imagens
      </p>
      
      <!-- Campo de Prompt condicional -->
      <div v-if="customImageInstructionsEnabled" class="mt-3 p-4 bg-n-slate-2 dark:bg-n-slate-3 rounded-lg border border-n-slate-4 animate-fade-in">
        <div class="flex items-center justify-between mb-2">
          <label class="text-sm font-medium text-n-slate-12">Instruções para Análise</label>
          <span v-if="imagePrompt" class="text-xs text-n-slate-10">{{ imagePrompt.length }}/2000</span>
        </div>
        <Textarea
          v-model="imagePrompt"
          placeholder="Ex: Analise a imagem enviada e identifique o produto ou documento. Se for um produto, descreva suas características principais. Se for um documento, extraia as informações relevantes."
          @blur="handleImagePromptBlur"
          :maxlength="2000"
          :autoHeight="true"
          :resize="true"
          :minHeight="'5rem'"
          :maxHeight="'15rem'"
          :showCharacterCount="false"
        />
        <ValidationFeedback 
          :value="imagePrompt"
          :field-type="'imagePrompt'"
          :touched="imagePromptIsTouched"
          :helper-text="customImageInstructionsEnabled ? 'Mínimo 10 caracteres, máximo 2000. Campo obrigatório.' : 'Máximo 2000 caracteres. Campo opcional.'"
          :show-helper="true"
          :show-char-count="true"
          :current-length="imagePrompt.length"
          :max-length="2000"
          :min-length="customImageInstructionsEnabled ? 10 : 0"
          :is-image-prompt-enabled="customImageInstructionsEnabled"
          @validation="handleImagePromptValidation"
        />
        <div class="text-xs text-n-slate-10 mt-2">
          O agente sempre pode ler imagens. Este campo define instruções adicionais para casos específicos.
        </div>
      </div>
    </div>

    <!-- Agrupamento de Mensagens -->
    <div class="flex flex-col gap-1.5">
      <div class="flex items-center gap-3">
        <label class="text-sm font-medium text-n-slate-12">Agrupamento de Mensagens</label>
        <Switch v-model="messageGroupingEnabled" />
      </div>
      <p class="text-n-slate-10 text-sm mt-1">
        <span class="i-lucide-message-square-plus mr-1"></span>
        Agrupar mensagens recebidas em sequência para respostas mais fluidas
      </p>
      
      <!-- Conteúdo condicional do agrupamento -->
      <div v-if="messageGroupingEnabled" class="mt-3 p-4 bg-n-slate-2 dark:bg-n-slate-3 rounded-lg border border-n-slate-4 animate-fade-in">
        <label class="text-sm font-medium text-n-slate-12 mb-2 block">Selecione o Intervalo</label>
        <div class="relative w-fit custom-dropdown-grouping">
          <!-- Botão do dropdown -->
          <button
            @click="toggleDropdown"
            type="button"
            class="flex items-center justify-between px-3 py-2 text-sm border rounded-lg bg-n-slate-1 border-n-slate-6 text-n-slate-12 hover:border-primary-600 focus:border-primary-600 focus:ring-2 focus:ring-primary-600/20 focus:outline-none transition-colors cursor-pointer min-w-[240px] dark:bg-n-slate-1 dark:border-n-slate-6 dark:text-n-slate-12 dark:hover:border-primary-600 dark:focus:border-primary-600 dark:focus:ring-primary-600/20"
          >
            <span>{{ selectedGroupingLabel }}</span>
            <span 
              class="i-lucide-chevron-up ml-2 h-4 w-4 text-n-slate-10 transition-transform"
              :class="{ 'rotate-180': !isDropdownOpen }"
            ></span>
          </button>
          
          <!-- Lista de opções -->
          <div
            v-if="isDropdownOpen"
            class="absolute bottom-full mb-1 left-0 w-full bg-n-slate-1 border border-n-slate-6 rounded-lg shadow-lg z-50 overflow-hidden"
          >
            <button
              v-for="option in messageGroupingOptions"
              :key="option.value"
              @click="selectGrouping(option)"
              type="button"
              class="w-full px-3 py-2 text-sm text-left hover:bg-n-slate-3 transition-colors op-select"
              :class="{ 'bg-n-slate-3': messageGrouping === option.value }"
            >
              {{ option.label }}
            </button>
          </div>
        </div>
        <div class="text-xs text-n-slate-10 mt-2">
          Mensagens recebidas dentro desse intervalo serão agrupadas em uma única resposta.
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@keyframes pulseShadow {
  0% {
    box-shadow: 0 0 0 0 rgba(246, 148, 157, 0.7);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 0 2px 5px rgba(246, 148, 157, 0);
    transform: scale(1.1);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(246, 148, 157, 0);
    transform: scale(1);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pulse-animation {
  animation: pulseShadow 2s infinite;
}

.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}

.info-btn {
  width: 20px !important;
  height: 20px !important;
  line-height: 1 !important;
}

.op-select {
  border-radius: 0 !important;
}

/* Container do slider para melhor organização visual */
.randomness-slider-container {
  padding: 16px;
  border-radius: 8px;
  transition: all 0.2s ease;
}

/* Estilos do slider melhorados */
.randomness-slider {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  position: relative;
}

/* Thumb (botão deslizante) - WebKit */
.randomness-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;
}

.randomness-slider::-webkit-slider-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.randomness-slider::-webkit-slider-thumb:active {
  transform: scale(1.05);
}

/* Firefox */
.randomness-slider::-moz-range-thumb {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transition: all 0.15s ease;
  border: none;
}

.randomness-slider::-moz-range-thumb:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.randomness-slider::-moz-range-track {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  border: none;
}

.randomness-slider::-moz-range-progress {
  height: 6px;
  border-radius: 3px;
}
</style>